// ============================================================
// Urgency statistics — every follow-up of the pilot period in the
// demo history (statsData.js) split into its channels. The first
// message goes out on the carrier's own channel; without a reply
// in 30 minutes it escalates to a voice call, then to the planner.
// Day totals match statsData, so the overview tab adds up.
// ============================================================

import { history, rng } from './statsData';

export const CHANNELS = ['sms', 'whatsapp', 'email', 'voice'];

// First-contact share follows the carriers' preferred channels in
// arrivalsData (SMS: D1 + D5, WhatsApp: D2, e-mail: D3, call: D4).
const FIRST_SHARE = { sms: 0.38, whatsapp: 0.25, email: 0.22, voice: 0.15 };
// Reply time range in minutes when the carrier answers.
const REPLY_MINUTES = { sms: [4, 25], whatsapp: [2, 18], email: [10, 45], voice: [1, 4] };
// Share of resolved follow-ups that still needed the escalation call.
const ESCALATED_RESOLVED = 0.22;
const ESCALATION_AFTER = 30;

// Assumptions behind the savings estimate, shown on the page.
export const ASSUMPTIONS = {
  plannerMinutes: 6, // planner's manual follow-up: find the contact, call, note the result
  workDaysPerMonth: 21,
  priceCzk: { sms: 1.5, whatsapp: 0.9, email: 0.05, voice: 4 }, // per message or call
};

function pickChannel(r) {
  let x = r();
  for (const c of CHANNELS) {
    x -= FIRST_SHARE[c];
    if (x <= 0) return c;
  }
  return 'sms';
}

const between = (r, [min, max]) => Math.round(min + r() * (max - min));

function buildFollowUps() {
  const r = rng(1300);
  return history.filter(d => d.pilot).map(day => {
    const items = [];
    for (let i = 0; i < day.urgencies; i += 1) {
      const first = pickChannel(r);
      const resolved = i < day.autoResolved;
      const escalated = first !== 'voice' && (!resolved || r() < ESCALATED_RESOLVED);
      const replyMinutes = !resolved ? null
        : escalated ? ESCALATION_AFTER + between(r, REPLY_MINUTES.voice)
          : between(r, REPLY_MINUTES[first]);
      items.push({ first, escalated, resolved, replyMinutes });
    }
    return { date: day.date, items };
  });
}

export const followUps = buildFollowUps();

const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

// Summary of the pilot days within the last `days` working days.
export function urgencySummary(days) {
  const from = history[history.length - days].date;
  const items = followUps.filter(d => d.date >= from).flatMap(d => d.items);
  const pilotDays = followUps.filter(d => d.date >= from).length;

  const channels = CHANNELS.map(id => {
    const firstHere = items.filter(u => u.first === id);
    const callsHere = id === 'voice' ? items.filter(u => u.escalated) : [];
    const sent = firstHere.length + callsHere.length;
    const repliedFirst = firstHere.filter(u => u.resolved && (!u.escalated || id === 'voice'));
    const repliedCall = callsHere.filter(u => u.resolved);
    const replies = [...repliedFirst, ...repliedCall];
    return {
      id,
      sent,
      replyPct: sent ? (replies.length / sent) * 100 : null,
      replyMedian: median(repliedFirst.map(u => u.replyMinutes)
        .concat(repliedCall.map(u => u.replyMinutes - ESCALATION_AFTER))),
      costCzk: sent * ASSUMPTIONS.priceCzk[id],
    };
  });

  const total = items.length;
  const resolved = items.filter(u => u.resolved).length;
  const perMonth = pilotDays ? ASSUMPTIONS.workDaysPerMonth / pilotDays : 0;
  return {
    pilotDays,
    total,
    replyPct: total ? (resolved / total) * 100 : 0,
    replyMedian: median(items.filter(u => u.resolved).map(u => u.replyMinutes)),
    escalatedPct: total ? (items.filter(u => u.escalated).length / total) * 100 : 0,
    autoResolved: resolved,
    toPlanner: total - resolved,
    channels,
    savedHoursMonth: (resolved * perMonth * ASSUMPTIONS.plannerMinutes) / 60,
    costCzkMonth: channels.reduce((s, c) => s + c.costCzk, 0) * perMonth,
  };
}
